import { useEffect, useSyncExternalStore } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { getAthenaClient } from 'components/api';
import {
  buildSaleHref,
  isActiveSaleTransaction,
} from 'components/buyer/hub/saleTransactionCardModel';
import { isActiveBuyerOffer } from 'helpers/buyerOfferStatus';
import { captureError } from 'helpers/captureError';

export type NavTransactionType = 'purchase' | 'sale';

export interface NavTransaction {
  id: string;
  type: NavTransactionType;
  /** Street line shown after the "Buy -" / "Sell -" prefix. */
  street: string;
  href: string;
  /** Status suffix appended for inactive seller offers, e.g. "Past offer"/"Withdrawn". */
  statusLabel?: string;
}

export const navTransactionLabel = (transaction: NavTransaction): string => {
  const prefix = transaction.type === 'purchase' ? 'Buy' : 'Sell';
  const base = `${prefix} - ${transaction.street}`;
  return transaction.statusLabel ? `${base} (${transaction.statusLabel})` : base;
};

// ---- Shared module-level store ----
// The same lean read powers several independently-mounted surfaces (the desktop
// dropdown, the mobile menu, and the always-mounted header that prefetches it),
// so a single `useSyncExternalStore` store fetches once and keeps every consumer
// in sync — mirroring the saved-searches store rather than a per-hook copy. It
// is only ever written client-side from `useEffect`, so module state shared
// across SSR requests can't leak between users (the fetch never runs on server).

let storeState: NavTransaction[] = [];
const listeners = new Set<() => void>();
// Bumped on every fetch and on logout so a response from a previous auth state
// can't overwrite fresher state.
let requestId = 0;
// Tracks the in-flight/settled load so concurrent consumers share one request
// and a completed load isn't refetched until auth changes.
let loadPromise: Promise<void> | null = null;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): NavTransaction[] => storeState;

const setStoreState = (next: NavTransaction[]) => {
  storeState = next;
  listeners.forEach((listener) => listener());
};

const requestNavTransactions = async (): Promise<void> => {
  const id = ++requestId;
  const data = await getAthenaClient().NavTransactions();
  if (id !== requestId) {
    return;
  }

  const purchases: NavTransaction[] = (data?.customerAccount?.buying?.offers ?? [])
    .filter((offer) => isActiveBuyerOffer(offer.transactionState))
    .map((offer) => ({
      id: offer.id,
      type: 'purchase',
      street: offer.listing?.property?.street ?? 'Your purchase',
      href: `/dashboard/buy/${offer.id}`,
    }));

  const sales: NavTransaction[] = (data?.customerAccount?.selling?.transactions ?? [])
    .map((transaction) => {
      const base: NavTransaction = {
        id: transaction.id,
        type: 'sale',
        street: transaction.address?.street ?? 'Your sale',
        href: buildSaleHref(transaction),
      };
      if (isActiveSaleTransaction(transaction)) {
        return base;
      }
      // Surface inactive/withdrawn seller offers so customers can refresh them
      // rather than hiding them from the account nav. `DeniedTransaction` covers
      // ineligible/denied properties (not just expirations) and this lean query
      // fetches no offer state that could prove expiration, so it gets the same
      // neutral "Past offer" label the hub card model uses rather than "Expired".
      if (transaction.__typename === 'DeniedTransaction') {
        return { ...base, statusLabel: 'Past offer' };
      }
      if (transaction.__typename === 'WithdrawnTransaction') {
        return { ...base, statusLabel: 'Withdrawn' };
      }
      // Closed/completed sales are no longer actionable from the nav.
      return null;
    })
    .filter((transaction): transaction is NavTransaction => transaction !== null);

  setStoreState([...purchases, ...sales]);
};

const fetchNavTransactions = (): Promise<void> => {
  if (!loadPromise) {
    loadPromise = requestNavTransactions().catch((error) => {
      captureError(error, 'NavTransactions');
      // Drop the cached promise so a later mount can retry after a failure.
      loadPromise = null;
    });
  }
  return loadPromise;
};

const clearNavTransactions = () => {
  requestId += 1;
  loadPromise = null;
  setStoreState([]);
};

/**
 * Resets the module-level store. Exported for tests (and HMR) to isolate state
 * between runs; not used by application code.
 */
export const __resetNavTransactionsStoreForTest = () => {
  requestId += 1;
  loadPromise = null;
  storeState = [];
};

// Keeps the shared store in sync with auth: load once when authenticated, clear
// on a definitive logout. `authenticating` is the transient initial/refresh
// state, ignored so a background re-render doesn't blank an open menu. The logout
// clear always runs so data can never leak to the next user.
const useNavTransactionsSync = (): void => {
  const { authentication } = useAuth();
  const authState = authentication?.state;
  const isLoggedIn = authState === 'authenticated';

  useEffect(() => {
    if (isLoggedIn) {
      void fetchNavTransactions();
      return;
    }
    if (authState === 'authenticating') {
      return;
    }
    clearNavTransactions();
  }, [isLoggedIn, authState]);
};

/**
 * Warms the shared nav-transactions store without subscribing to it. Mount this
 * in an always-rendered header slot (e.g. `AccountSection`) so the request is in
 * flight before any menu opens — important for the mobile menu, whose list only
 * mounts on open.
 */
export const usePrefetchNavTransactions = (): void => {
  useNavTransactionsSync();
};

/**
 * Active purchase offers and sale transactions for the account navigation,
 * prefetched on auth and shared across every header surface.
 */
export const useNavTransactions = (): NavTransaction[] => {
  useNavTransactionsSync();
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};
